/* eslint-disable react-refresh/only-export-components */
import { createContext, useContext, useMemo, type ReactNode } from 'react';
import type { HttpClient } from '@shared/http/http-client';
import type { IUserPreferencesRepository } from '@domain/data-contracts/user-preferences-repository.interface';
import type { IOrderRepository } from '@domain/data-contracts/order-repository.interface';
import type { IInvoiceRepository } from '@domain/data-contracts/invoice-repository.interface';
import type { IShipmentRepository } from '@domain/data-contracts/shipment-repository.interface';
import type { ICaseRepository } from '@domain/data-contracts/case-repository.interface';
import type { IProductionAssignmentRepository } from '@domain/data-contracts/production-assignment-repository.interface';
import type { IClientPortalRepository } from '@domain/data-contracts/client-portal-repository.interface';
import type { IEmployeePortalRepository } from '@domain/data-contracts/employee-portal-repository.interface';
import type { ITaskQueueRepository } from '@domain/data-contracts/task-queue-repository.interface';
import type { IDeliveryNoteRepository } from '@domain/data-contracts/delivery-note-repository.interface';
import type { IClientRepository } from '@domain/data-contracts/client-repository.interface';
import type { IPatientRepository } from '@domain/data-contracts/patient-repository.interface';
import type { IWorkpieceTypeRepository } from '@domain/data-contracts/workpiece-type-repository.interface';
import type { IResourceRepository } from '@domain/data-contracts/resource-repository.interface';
import type { IEmployeeRepository } from '@domain/data-contracts/employee-repository.interface';
import type { ILabRouteRepository } from '@domain/data-contracts/lab-route-repository.interface';
import type { IProductionPartnerRepository } from '@domain/data-contracts/production-partner-repository.interface';
import type { IDesignRequestRepository } from '@domain/data-contracts/design-request-repository.interface';
import type { ILabAdminRepository } from '@domain/data-contracts/lab-admin-repository.interface';
import type { ILabRepository } from '@domain/data-contracts/lab-repository.interface';
import type { IScanConnectionRepository } from '@domain/data-contracts/scan-connection-repository.interface';
import type { IScanConnectionDetailRepository } from '@domain/data-contracts/scan-connection-detail-repository.interface';
import type { IPlatformDashboardRepository } from '@domain/data-contracts/platform-dashboard-repository.interface';
import type { IInvitationRepository } from '@domain/data-contracts/invitation-repository.interface';
import type { IOrganizationRepository } from '@domain/data-contracts/organization-repository.interface';
import type { IOrderChatRepository } from '@domain/data-contracts/order-chat-repository.interface';
import { UserPreferencesRepository } from '@data/api-repositories/user-preferences-repository.class';
import { OrderRepository } from '@data/api-repositories/order-repository.class';
import { InvoiceRepository } from '@data/api-repositories/invoice-repository.class';
import { ShipmentRepository } from '@data/api-repositories/shipment-repository.class';
import { CaseRepository } from '@data/api-repositories/case-repository.class';
import { ProductionAssignmentRepository } from '@data/api-repositories/production-assignment-repository.class';
import { ClientPortalRepository } from '@data/api-repositories/client-portal-repository.class';
import { EmployeePortalRepository } from '@data/api-repositories/employee-portal-repository.class';
import { IndexedDbTaskQueueRepository } from '@data/browser-repositories/task-queue-repository.class';
import { DeliveryNoteRepository } from '@data/api-repositories/delivery-note-repository.class';
import { ClientRepository } from '@data/api-repositories/client-repository.class';
import { PatientRepository } from '@data/api-repositories/patient-repository.class';
import { WorkpieceTypeRepository } from '@data/api-repositories/workpiece-type-repository.class';
import { ResourceRepository } from '@data/api-repositories/resource-repository.class';
import { EmployeeRepository } from '@data/api-repositories/employee-repository.class';
import { LabRouteRepository } from '@data/api-repositories/lab-route-repository.class';
import { ProductionPartnerRepository } from '@data/api-repositories/production-partner-repository.class';
import { DesignRequestRepository } from '@data/api-repositories/design-request-repository.class';
import { LabAdminRepository } from '@data/api-repositories/lab-admin-repository.class';
import { LabRepository } from '@data/api-repositories/lab-repository.class';
import { ScanConnectionRepository } from '@data/api-repositories/scan-connection-repository.class';
import { ScanConnectionDetailRepository } from '@data/api-repositories/scan-connection-detail-repository.class';
import { PlatformDashboardRepository } from '@data/api-repositories/platform-dashboard-repository.class';
import { InvitationRepository } from '@data/api-repositories/invitation-repository.class';
import { OrganizationRepository } from '@data/api-repositories/organization-repository.class';
import { OrderChatRepository } from '@data/api-repositories/order-chat-repository.class';

/**
 * Binds each `@domain/data-contracts` interface to a concrete
 * implementation from `3.data`. Populated by scheduler tasks as
 * features come online.
 */
export interface Repositories {
  readonly userPreferencesRepository: IUserPreferencesRepository;
  readonly orderRepository: IOrderRepository;
  readonly invoiceRepository: IInvoiceRepository;
  readonly shipmentRepository: IShipmentRepository;
  readonly caseRepository: ICaseRepository;
  readonly productionAssignmentRepository: IProductionAssignmentRepository;
  readonly clientPortalRepository: IClientPortalRepository;
  readonly employeePortalRepository: IEmployeePortalRepository;
  readonly taskQueueRepository: ITaskQueueRepository;
  readonly deliveryNoteRepository: IDeliveryNoteRepository;
  readonly clientRepository: IClientRepository;
  readonly patientRepository: IPatientRepository;
  readonly workpieceTypeRepository: IWorkpieceTypeRepository;
  readonly resourceRepository: IResourceRepository;
  readonly employeeRepository: IEmployeeRepository;
  readonly labRouteRepository: ILabRouteRepository;
  readonly productionPartnerRepository: IProductionPartnerRepository;
  readonly designRequestRepository: IDesignRequestRepository;
  readonly labAdminRepository: ILabAdminRepository;
  readonly labRepository: ILabRepository;
  readonly scanConnectionRepository: IScanConnectionRepository;
  readonly scanConnectionDetailRepository: IScanConnectionDetailRepository;
  readonly platformDashboardRepository: IPlatformDashboardRepository;
  readonly invitationRepository: IInvitationRepository;
  readonly organizationRepository: IOrganizationRepository;
  readonly orderChatRepository: IOrderChatRepository;
}

const RepositoriesContext = createContext<Repositories | null>(null);

type Props = {
  readonly http: HttpClient;
  readonly gatewaysHttp?: HttpClient;
  readonly children: ReactNode;
  readonly overrides?: Partial<Repositories>;
};

/**
 * Composition root for the `3.data` layer.
 *
 * Receives a shared `HttpClient` and builds the `Repositories`
 * map once per mount. Pages consume it indirectly via the
 * `ServicesProvider` — never directly. `overrides` exists for
 * tests that want to swap a single repository with a fake.
 */
export function RepositoriesProvider({ http, gatewaysHttp, overrides, children }: Props): JSX.Element {
  const value = useMemo<Repositories>(
    () => ({
      userPreferencesRepository: new UserPreferencesRepository(http),
      orderRepository: new OrderRepository(http),
      invoiceRepository: new InvoiceRepository(http),
      shipmentRepository: new ShipmentRepository(http),
      caseRepository: new CaseRepository(http),
      productionAssignmentRepository: new ProductionAssignmentRepository(http),
      clientPortalRepository: new ClientPortalRepository(http),
      employeePortalRepository: new EmployeePortalRepository(http),
      taskQueueRepository: new IndexedDbTaskQueueRepository(),
      deliveryNoteRepository: new DeliveryNoteRepository(http),
      clientRepository: new ClientRepository(http),
      patientRepository: new PatientRepository(http),
      workpieceTypeRepository: new WorkpieceTypeRepository(http),
      resourceRepository: new ResourceRepository(http),
      employeeRepository: new EmployeeRepository(http),
      labRouteRepository: new LabRouteRepository(http),
      productionPartnerRepository: new ProductionPartnerRepository(http),
      designRequestRepository: new DesignRequestRepository(http),
      labAdminRepository: new LabAdminRepository(http),
      labRepository: new LabRepository(http),
      scanConnectionRepository: new ScanConnectionRepository(gatewaysHttp ?? http),
      scanConnectionDetailRepository: new ScanConnectionDetailRepository(gatewaysHttp ?? http),
      platformDashboardRepository: new PlatformDashboardRepository(
        new LabAdminRepository(http),
        new ScanConnectionRepository(gatewaysHttp ?? http),
      ),
      invitationRepository: new InvitationRepository(http),
      organizationRepository: new OrganizationRepository(http),
      orderChatRepository: new OrderChatRepository(http),
      ...overrides,
    }),
    [http, gatewaysHttp, overrides],
  );

  return <RepositoriesContext.Provider value={value}>{children}</RepositoriesContext.Provider>;
}

export function useRepositories(): Repositories {
  const ctx = useContext(RepositoriesContext);
  if (!ctx) {
    throw new Error('useRepositories must be used within a RepositoriesProvider');
  }
  return ctx;
}

// ---------------------------------------------------------------------------
// Gateways HTTP client context
// ---------------------------------------------------------------------------

const GatewaysHttpClientContext = createContext<HttpClient | null>(null);

type GatewaysHttpClientProviderProps = {
  readonly client: HttpClient;
  readonly children: ReactNode;
};

/**
 * Provides the Gateways API HTTP client (distinct auth scope from LabFlow)
 * to any descendant that calls `useGatewaysHttpClient`.
 */
export function GatewaysHttpClientProvider({
  client,
  children,
}: GatewaysHttpClientProviderProps): JSX.Element {
  return (
    <GatewaysHttpClientContext.Provider value={client}>
      {children}
    </GatewaysHttpClientContext.Provider>
  );
}

export function useGatewaysHttpClient(): HttpClient {
  const ctx = useContext(GatewaysHttpClientContext);
  if (!ctx) {
    throw new Error('useGatewaysHttpClient must be used within a GatewaysHttpClientProvider');
  }
  return ctx;
}
